/* assets/adaptive-dual-nback.js
 * Adaptive Dual N-Back Trainer — Shopify-safe audio + robust highlighting
 * Reads audio URLs from #nback-app data-sounds (no globals, no timing issues)
 *
 * KEY ROBUSTNESS FIX:
 * - render() rebuilds DOM frequently (innerHTML), which can wipe highlight classes.
 * - We store activePos + activeUntil in state, and repaint after every render.
 * - We also cancel/guard highlight timeouts so stale timers can’t clear the new trial.
 */

(() => {
  const root = document.getElementById('nback-app');
  if (!root) return;

  const APP_NAME = root.dataset.appName || "Adaptive Dual N-Back Trainer";

  // ======= Flow band =======
  const TARGET_ACC_LO = 70;
  const TARGET_ACC_HI = 85;

  // ======= Real-time adaptation =======
  const ADAPT_EVERY_TRIALS = 10;
  const ADAPT_WINDOW_TRIALS = 10;
  const ADAPT_COOLDOWN_MS = 2500;
  const MIN_EVENTS_FOR_ADAPT = 8;

  // Smaller step so it doesn't suddenly feel too fast
  const STIM_STEP = 200;

  // ======= Visual timing =======
  // Total highlight = stimMs + HOLD_EXTRA_MS
  const HOLD_EXTRA_MS = 3600; // <-- increase for longer highlight

  // ======= Pacing (gap between trials) =======
  // After scoring finishes, wait GAP_MS then start next trial
  const GAP_MS = 120; // <-- decrease for shorter time between stimuli

  // ======= Letters =======
  const LETTERS = ["A","B","C","F","H","K","J","L","O"];

  // IMPORTANT: Raise stimMin so adaptation can’t make it “flashy”
  const KNOB_LIMITS = {
    stimMin: 2200,
    stimMax: 8000,
    graceMin: 500,
    graceMax: 2600
  };

  // Defaults
  const DEFAULTS = {
    stimMs: 4200,
    graceMs: 1200,
    feedbackMs: 320
  };

  const html = (s, ...v) => s.map((x,i)=> x + (v[i] ?? '')).join('');
  const now = () => Date.now();
  const clamp = (n,a,b) => Math.max(a, Math.min(b, n));

  function pct(h,m,fa,cr){
    const t = h+m+fa+cr;
    return t ? Math.round(100*(h+cr)/t) : 0;
  }
  function formatTime(ms){
    const s = Math.max(0, Math.floor(ms/1000));
    const m = Math.floor(s/60);
    const r = s%60;
    return `${m}:${String(r).padStart(2,'0')}`;
  }

  const state = {
    running: false,
    paused: false,

    n: 2,
    totalMin: 10,
    roundSec: 60,
    soundOn: true,

    knobs: { ...DEFAULTS },
    lastAdjust: "—",
    lastAdjustAt: 0,

    startedAt: null,
    roundStartedAt: null,

    trialIndex: 0,
    roundIndex: 0,

    history: [],
    current: null,
    pending: { audio:false, loc:false },

    timeoutId: null,
    uiTickId: null,

    audioCueUntil: 0,
    outcomes: [],

    // Robust highlight control (works even when render() rebuilds DOM)
    highlightTimeoutId: null,
    highlightToken: 0,
    activePos: null,
    activeUntil: 0,

    round: {
      audio: { hits:0, misses:0, falseAlarms:0, correctRejects:0 },
      loc:   { hits:0, misses:0, falseAlarms:0, correctRejects:0 }
    },
    total: {
      audio: { hits:0, misses:0, falseAlarms:0, correctRejects:0 },
      loc:   { hits:0, misses:0, falseAlarms:0, correctRejects:0 }
    }
  };

  // ==========================================================
  // AUDIO (reads from root.dataset.sounds)
  // ==========================================================
  const audioCache = {};
  let audioInitialized = false;

  function readSoundsFromDataset(){
    const raw = root.dataset.sounds || "";
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.error("[NBack] Failed to parse data-sounds JSON:", e, raw);
      return {};
    }
  }

  function preloadAudio(){
    for (const k of Object.keys(audioCache)) delete audioCache[k];

    const files = readSoundsFromDataset();
    let count = 0;

    for (const key of LETTERS){
      const url = files[key];
      if (!url) continue;

      const a = new Audio(url);
      a.preload = "auto";
      a.crossOrigin = "anonymous";
      a.addEventListener("error", () => {
        console.warn("[NBack] audio error", key, url, a.error);
      });

      audioCache[key] = a;
      count++;
    }

    return count;
  }

  async function unlockAudio(){
    // Must be called from a user gesture (Start click)
    try {
      const firstKey = Object.keys(audioCache)[0];
      const a = audioCache[firstKey];
      if (!a) return;

      a.muted = true;
      await a.play();
      a.pause();
      a.currentTime = 0;
      a.muted = false;
    } catch (e) {
      console.warn("[NBack] unlockAudio blocked:", e);
    }
  }

  function ensureAudioReady(){
    const files = readSoundsFromDataset();
    const keys = Object.keys(files);

    if (keys.length === 0) {
      audioInitialized = false;
      console.error("[NBack] No sounds found in data-sounds. Audio will be missing.");
      return;
    }

    preloadAudio();
    audioInitialized = true;
  }

  function playSound(letter){
    if (!state.soundOn) return;

    const a = audioCache[letter];
    if (!a) {
      console.warn("[NBack] missing audio for", letter, "cache keys:", Object.keys(audioCache));
      return;
    }

    try {
      // clone avoids overlap/play-interrupted issues
      const b = a.cloneNode(true);
      b.play().catch(() => {});
    } catch (e) {
      console.warn("[NBack] playSound exception:", e);
    }
  }

  // ======= Timers =======
  function getTotalRemainMs(){
    if (!state.startedAt) return state.totalMin * 60 * 1000;
    const limit = state.totalMin * 60 * 1000;
    return Math.max(0, limit - (now() - state.startedAt));
  }
  function getRoundRemainMs(){
    if (!state.roundStartedAt) return state.roundSec * 1000;
    const limit = state.roundSec * 1000;
    return Math.max(0, limit - (now() - state.roundStartedAt));
  }

  // ======= Scoring =======
  function resetRoundScores(){
    state.round.audio = { hits:0, misses:0, falseAlarms:0, correctRejects:0 };
    state.round.loc   = { hits:0, misses:0, falseAlarms:0, correctRejects:0 };
  }
  function resetTotalScores(){
    state.total.audio = { hits:0, misses:0, falseAlarms:0, correctRejects:0 };
    state.total.loc   = { hits:0, misses:0, falseAlarms:0, correctRejects:0 };
  }
  function applySignal(roundObj, totalObj, isMatch, responded){
    if (isMatch && responded){ roundObj.hits++; totalObj.hits++; return; }
    if (isMatch && !responded){ roundObj.misses++; totalObj.misses++; return; }
    if (!isMatch && responded){ roundObj.falseAlarms++; totalObj.falseAlarms++; return; }
    roundObj.correctRejects++; totalObj.correctRejects++;
  }

  function windowAccuracy(){
    const w = state.outcomes.slice(-ADAPT_WINDOW_TRIALS);
    if (!w.length) return null;

    let aCorrect=0, aTotal=0, lCorrect=0, lTotal=0;
    for (const t of w){
      const aOk = (t.aMatch && t.aResp) || (!t.aMatch && !t.aResp);
      const lOk = (t.lMatch && t.lResp) || (!t.lMatch && !t.lResp);
      aCorrect += aOk ? 1 : 0; aTotal += 1;
      lCorrect += lOk ? 1 : 0; lTotal += 1;
    }
    const aAcc = aTotal ? Math.round(100 * aCorrect / aTotal) : 0;
    const lAcc = lTotal ? Math.round(100 * lCorrect / lTotal) : 0;
    const blended = Math.round((aAcc + lAcc) / 2);
    return { aAcc, lAcc, blended, trials: w.length };
  }

  // ======= UI =======
  function render(){
    const rA = state.round.audio, rL = state.round.loc;
    const tA = state.total.audio, tL = state.total.loc;

    const roundAccA = pct(rA.hits,rA.misses,rA.falseAlarms,rA.correctRejects);
    const roundAccL = pct(rL.hits,rL.misses,rL.falseAlarms,rL.correctRejects);
    const totalAccA = pct(tA.hits,tA.misses,tA.falseAlarms,tA.correctRejects);
    const totalAccL = pct(tL.hits,tL.misses,tL.falseAlarms,tL.correctRejects);

    const totalRemain = state.running ? getTotalRemainMs() : (state.totalMin * 60 * 1000);
    const roundRemain = state.running ? getRoundRemainMs() : (state.roundSec * 1000);

    const totalLimitMs = state.totalMin * 60 * 1000;
    const roundLimitMs = state.roundSec * 1000;

    const totalElapsed = state.startedAt ? (now() - state.startedAt) : 0;
    const roundElapsed = state.roundStartedAt ? (now() - state.roundStartedAt) : 0;

    const totalProg = state.running ? Math.round(100 * (totalElapsed / totalLimitMs)) : 0;
    const roundProg = state.running ? Math.round(100 * (roundElapsed / roundLimitMs)) : 0;

    const dotActive = state.running && (now() <= state.audioCueUntil);
    const currentSound = state.current ? LETTERS[state.current.snd] : "—";
    const w = windowAccuracy();

    const soundKeys = Object.keys(readSoundsFromDataset() || {});
    const soundKeyStr = soundKeys.length ? soundKeys.join(",") : "—";

    const highlightMs = state.current ? (state.knobs.stimMs + HOLD_EXTRA_MS) : 0;
    const effectiveIsi = state.knobs.stimMs + state.knobs.graceMs + GAP_MS;

    root.innerHTML = html`
      <div class="adb-card">
        <div class="adb-row">
          <div>
            <div class="adb-title">${APP_NAME}</div>
            <div class="adb-muted">
              Match the <b>location</b> or <b>sound</b> from <b>N</b> trials back.
              Desktop: <span class="adb-kbd">A</span> audio · <span class="adb-kbd">L</span> location.
            </div>
          </div>
          <div class="adb-spacer"></div>
          <div class="adb-muted" style="font-weight:950">${state.running ? (state.paused ? 'Paused' : 'Running') : 'Ready'}</div>
        </div>

        <div class="adb-row" style="margin-top:14px">
          <label class="adb-muted">N:
            <select id="adb-nSel" class="adb-select" ${state.running ? 'disabled' : ''}>
              ${[1,2,3,4,5].map(n => `<option value="${n}" ${n===state.n?'selected':''}>${n}</option>`).join('')}
            </select>
          </label>

          <label class="adb-muted">Round:
            <select id="adb-roundSel" class="adb-select" ${state.running ? 'disabled' : ''}>
              ${[60,75,90].map(s => `<option value="${s}" ${s===state.roundSec?'selected':''}>${s}s</option>`).join('')}
            </select>
          </label>

          <label class="adb-muted">Total:
            <select id="adb-totalSel" class="adb-select" ${state.running ? 'disabled' : ''}>
              ${[5,10,15,20].map(m => `<option value="${m}" ${m===state.totalMin?'selected':''}>${m} min</option>`).join('')}
            </select>
          </label>

          <label class="adb-muted">Audio:
            <select id="adb-sndSel" class="adb-select" ${state.running ? 'disabled' : ''}>
              <option value="on" ${state.soundOn?'selected':''}>On</option>
              <option value="off" ${!state.soundOn?'selected':''}>Off</option>
            </select>
          </label>

          <button id="adb-startBtn" class="adb-btn adb-btn-primary" ${state.running ? 'disabled' : ''}>Start</button>
          <button id="adb-pauseBtn" class="adb-btn" ${!state.running ? 'disabled' : ''}>${state.paused ? 'Resume' : 'Pause'}</button>
          <button id="adb-resetBtn" class="adb-btn">Reset</button>
        </div>

        ${(!state.soundOn) ? `
          <div class="adb-audioCueRow">
            <div class="adb-audioDot ${dotActive ? 'active' : ''}"></div>
            <div class="adb-audioLetter"><span style="font-weight:950">${state.running ? currentSound : '—'}</span></div>
          </div>
        ` : ``}

        <div class="adb-layout">
          <div class="adb-grid" id="adb-grid" aria-label="9-square grid">
            ${Array.from({length:9}).map((_,i)=> `<div class="adb-cell" data-i="${i}">&nbsp;</div>`).join('')}
          </div>

          <div class="adb-actions">
            <button id="adb-audioBtn" class="adb-actionBtn" ${!state.running || state.paused ? 'disabled' : ''}>
              Audio Match
              <span class="adb-small">Key: A · Tap on mobile</span>
            </button>
            <button id="adb-locBtn" class="adb-actionBtn" ${!state.running || state.paused ? 'disabled' : ''}>
              Location Match
              <span class="adb-small">Key: L · Tap on mobile</span>
            </button>
          </div>

          <div class="adb-hud">
            <div class="adb-stat">
              <b>Round remaining</b>
              <span>${formatTime(roundRemain)}</span>
              <div class="adb-muted" style="margin-top:8px; font-weight:950">Round progress</div>
              <div class="adb-bar"><i style="width:${Math.min(100, Math.max(0, roundProg))}%"></i></div>

              <div class="adb-muted" style="margin-top:12px; font-weight:950">Total remaining</div>
              <div><span style="font-size:20px; font-weight:950">${formatTime(totalRemain)}</span></div>
              <div class="adb-bar" style="margin-top:8px"><i style="width:${Math.min(100, Math.max(0, totalProg))}%"></i></div>
            </div>

            <div class="adb-stat">
              <b>Round accuracy</b>
              <span>Audio ${roundAccA}% · Location ${roundAccL}%</span>

              <div class="adb-muted" style="margin-top:10px; font-weight:950">Session accuracy</div>
              <div>
                <span class="adb-pill">Audio ${totalAccA}%</span>
                <span class="adb-pill">Location ${totalAccL}%</span>
              </div>
            </div>
          </div>

          <div class="adb-debug">
            <div class="ttl">Debug / Metrics</div>
            <div>last: <code>${state.lastAdjust}</code> window: <code>${w ? `blend=${w.blended}%` : "—"}</code></div>
            <div>timing: <code>STIM=${state.knobs.stimMs}</code> <code>GRACE=${state.knobs.graceMs}</code> <code>HOLD=${HOLD_EXTRA_MS}</code> <code>GAP=${GAP_MS}</code> <code>effISI=${effectiveIsi}</code></div>
            <div>highlight (this trial): <code>${highlightMs}</code> ms</div>
            <div>data-sounds keys: <code>${soundKeyStr}</code></div>
            <div>audio loaded: <code>${LETTERS.map(L => `${L}:${audioCache[L] ? "ok" : "missing"}`).join(" ")}</code></div>
            <div>audioInitialized: <code>${audioInitialized ? "true" : "false"}</code></div>
            <div>activePos: <code>${state.activePos ?? "—"}</code> activeUntil(ms): <code>${state.activeUntil ? (state.activeUntil - now()) : 0}</code></div>
          </div>
        </div>
      </div>
    `;

    // Bind UI
    const nSel = root.querySelector('#adb-nSel');
    const roundSel = root.querySelector('#adb-roundSel');
    const totalSel = root.querySelector('#adb-totalSel');
    const sndSel = root.querySelector('#adb-sndSel');

    const startBtn = root.querySelector('#adb-startBtn');
    const pauseBtn = root.querySelector('#adb-pauseBtn');
    const resetBtn = root.querySelector('#adb-resetBtn');

    const audioBtn = root.querySelector('#adb-audioBtn');
    const locBtn = root.querySelector('#adb-locBtn');

    if (nSel) nSel.onchange = () => { state.n = Number(nSel.value); };
    if (roundSel) roundSel.onchange = () => { state.roundSec = Number(roundSel.value); };
    if (totalSel) totalSel.onchange = () => { state.totalMin = Number(totalSel.value); };
    if (sndSel) sndSel.onchange = () => { state.soundOn = (sndSel.value === 'on'); };

    if (startBtn) startBtn.onclick = () => startSession();
    if (pauseBtn) pauseBtn.onclick = () => togglePause();
    if (resetBtn) resetBtn.onclick = () => resetAll();

    if (audioBtn) bindPress(audioBtn, () => registerResponse('audio'));
    if (locBtn) bindPress(locBtn, () => registerResponse('loc'));

    // IMPORTANT: render() rebuilds DOM; repaint highlight from state every time
    paintActiveFromState();
  }

  function bindPress(el, fn){
    const on = (e) => { e.preventDefault(); fn(); };
    el.onpointerdown = on;
    el.onclick = on;
  }

  // --- Robust highlight: state-driven, survives DOM rebuilds ---
  function paintActiveFromState(){
    const grid = root.querySelector('#adb-grid');
    if (!grid) return;

    grid.querySelectorAll('.adb-cell').forEach(el => el.classList.remove('adb-cell-active'));

    if (state.activePos == null) return;
    if (now() > state.activeUntil) return;

    const cell = grid.querySelector(`.adb-cell[data-i="${state.activePos}"]`);
    if (cell) cell.classList.add('adb-cell-active');
  }

  function setActive(i){
    state.activePos = i;
    paintActiveFromState();
  }

  function clearActive(){
    state.activePos = null;
    state.activeUntil = 0;

    const grid = root.querySelector('#adb-grid');
    if (!grid) return;
    grid.querySelectorAll('.adb-cell').forEach(el => el.classList.remove('adb-cell-active'));
  }

  function scheduleHighlightClear(ms){
    if (state.highlightTimeoutId) clearTimeout(state.highlightTimeoutId);
    state.highlightTimeoutId = null;

    const token = ++state.highlightToken;

    state.highlightTimeoutId = setTimeout(() => {
      if (token !== state.highlightToken) return;
      clearActive(); // clears DOM + state
    }, ms);
  }

  function flashButton(kind, ok){
    const btn = root.querySelector(kind === 'audio' ? '#adb-audioBtn' : '#adb-locBtn');
    if (!btn) return;
    btn.classList.remove('adb-ok','adb-bad');
    btn.classList.add(ok ? 'adb-ok' : 'adb-bad');
    setTimeout(() => btn.classList.remove('adb-ok','adb-bad'), state.knobs.feedbackMs);
  }

  function withinResponseWindow(){
    if (!state.current) return false;
    const dt = now() - state.current.t0;
    return dt >= 0 && dt <= (state.knobs.stimMs + state.knobs.graceMs);
  }

  function registerResponse(kind){
    if (!state.running || state.paused) return;
    if (!withinResponseWindow()) return;

    if (kind === 'audio'){
      state.pending.audio = true;
      flashButton('audio', !!state.current?.sndMatch);
    } else {
      state.pending.loc = true;
      flashButton('loc', !!state.current?.posMatch);
    }
  }

  window.addEventListener('keydown', (e) => {
    if (!state.running || state.paused) return;
    const k = (e.key || '').toLowerCase();
    if (k === 'a'){ e.preventDefault(); registerResponse('audio'); }
    if (k === 'l'){ e.preventDefault(); registerResponse('loc'); }
  }, { passive:false });

  function startSession(){
    if (state.running) return;
    state.running = true;
    state.paused = false;

    state.knobs = { ...DEFAULTS };
    state.lastAdjust = "—";
    state.lastAdjustAt = 0;

    state.startedAt = now();
    state.roundStartedAt = now();

    state.trialIndex = 0;
    state.roundIndex = 0;
    state.history = [];
    state.current = null;
    state.pending = { audio:false, loc:false };
    state.audioCueUntil = 0;
    state.outcomes = [];

    state.activePos = null;
    state.activeUntil = 0;
    if (state.highlightTimeoutId) clearTimeout(state.highlightTimeoutId);
    state.highlightTimeoutId = null;
    state.highlightToken = 0;

    resetRoundScores();
    resetTotalScores();

    ensureAudioReady();
    unlockAudio();

    render();
    scheduleNextTrial(0);
    startUiTick();
  }

  function togglePause(){
    if (!state.running) return;
    state.paused = !state.paused;

    if (state.paused){
      if (state.timeoutId) clearTimeout(state.timeoutId);
      state.timeoutId = null;

      if (state.highlightTimeoutId) clearTimeout(state.highlightTimeoutId);
      state.highlightTimeoutId = null;

      stopUiTick();
      clearActive();

      for (const L of LETTERS){
        const a = audioCache[L];
        if (!a) continue;
        try { a.pause(); a.currentTime = 0; } catch {}
      }
    } else {
      scheduleNextTrial(0);
      startUiTick();
    }
    render();
  }

  function resetAll(){
    if (state.timeoutId) clearTimeout(state.timeoutId);
    state.timeoutId = null;

    if (state.highlightTimeoutId) clearTimeout(state.highlightTimeoutId);
    state.highlightTimeoutId = null;

    stopUiTick();

    state.running = false;
    state.paused = false;

    state.startedAt = null;
    state.roundStartedAt = null;

    state.trialIndex = 0;
    state.roundIndex = 0;
    state.history = [];
    state.current = null;
    state.pending = { audio:false, loc:false };
    state.audioCueUntil = 0;
    state.outcomes = [];

    state.activePos = null;
    state.activeUntil = 0;
    state.highlightToken = 0;

    state.knobs = { ...DEFAULTS };
    state.lastAdjust = "—";
    state.lastAdjustAt = 0;

    resetRoundScores();
    resetTotalScores();

    clearActive();
    render();
  }

  function endRound(){
    resetRoundScores();
    state.roundIndex += 1;
    state.roundStartedAt = now();
  }

  function endSession(){
    state.running = false;
    state.paused = false;
    if (state.timeoutId) clearTimeout(state.timeoutId);
    state.timeoutId = null;

    if (state.highlightTimeoutId) clearTimeout(state.highlightTimeoutId);
    state.highlightTimeoutId = null;

    stopUiTick();
    clearActive();
    render();
  }

  function scheduleNextTrial(delayMs){
    if (!state.running || state.paused) return;
    if (getTotalRemainMs() <= 0){ endSession(); return; }
    if (getRoundRemainMs() <= 0){ endRound(); }
    state.timeoutId = setTimeout(runTrial, delayMs);
  }

  function runTrial(){
    if (!state.running || state.paused) return;

    if (getTotalRemainMs() <= 0){ endSession(); return; }
    if (getRoundRemainMs() <= 0){ endRound(); }

    state.pending = { audio:false, loc:false };

    const pos = Math.floor(Math.random()*9);
    const snd = Math.floor(Math.random()*9);
    const t0 = now();

    const canMatch = state.history.length >= state.n;
    const nBack = canMatch ? state.history[state.history.length - state.n] : null;

    const posMatch = canMatch ? (pos === nBack.pos) : false;
    const sndMatch = canMatch ? (snd === nBack.snd) : false;

    state.current = { pos, snd, t0, posMatch, sndMatch };

    const highlightMs = state.knobs.stimMs + HOLD_EXTRA_MS;

    // Robust: store highlight window in state, so render() can repaint
    state.activeUntil = now() + highlightMs;
    setActive(pos);

    // keep dot active for full visible window
    state.audioCueUntil = now() + highlightMs;

    playSound(LETTERS[snd]);

    // Hard clear at end (also cancels/guards old timeouts)
    scheduleHighlightClear(highlightMs);

    const scoreAt = state.knobs.stimMs + state.knobs.graceMs;

    setTimeout(() => {
      const aResp = !!state.pending.audio;
      const lResp = !!state.pending.loc;

      applySignal(state.round.audio, state.total.audio, sndMatch, aResp);
      applySignal(state.round.loc,   state.total.loc,   posMatch, lResp);

      state.outcomes.push({ aMatch: sndMatch, aResp, lMatch: posMatch, lResp });

      state.history.push({ pos, snd });
      state.trialIndex += 1;

      // Adapt stimMs only (gap stays consistent)
      if (state.outcomes.length > 0 && state.outcomes.length % ADAPT_EVERY_TRIALS === 0) {
        const t = now();
        if (t - state.lastAdjustAt >= ADAPT_COOLDOWN_MS) {
          const w = windowAccuracy();
          if (w && w.trials >= MIN_EVENTS_FOR_ADAPT) {
            const acc = w.blended;

            if (acc < TARGET_ACC_LO){
              state.knobs.stimMs = clamp(state.knobs.stimMs + STIM_STEP, KNOB_LIMITS.stimMin, KNOB_LIMITS.stimMax);
              state.lastAdjust = `slower (t${state.outcomes.length}, ${acc}%)`;
              state.lastAdjustAt = t;
            } else if (acc > TARGET_ACC_HI){
              state.knobs.stimMs = clamp(state.knobs.stimMs - STIM_STEP, KNOB_LIMITS.stimMin, KNOB_LIMITS.stimMax);
              state.lastAdjust = `faster (t${state.outcomes.length}, ${acc}%)`;
              state.lastAdjustAt = t;
            } else {
              state.lastAdjust = `hold (t${state.outcomes.length}, ${acc}%)`;
              state.lastAdjustAt = t;
            }
          }
        }
      }

      render();

      // Fixed, short gap between trials
      scheduleNextTrial(GAP_MS);
    }, scoreAt);
  }

  function startUiTick(){
    stopUiTick();
    state.uiTickId = setInterval(() => {
      if (!state.running || state.paused) return;
      if (getTotalRemainMs() <= 0){ endSession(); return; }
      if (getRoundRemainMs() <= 0){ endRound(); }
      render();
    }, 250);
  }

  function stopUiTick(){
    if (state.uiTickId) clearInterval(state.uiTickId);
    state.uiTickId = null;
  }

  // initial render
  render();
})();
